import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Building2, Search, Sparkles, ShieldAlert, CheckCircle, XCircle,
  AlertTriangle, FileText, Trash2, Loader2, Calendar, MapPin, Receipt, TrendingUp
} from "lucide-react";
import { toast } from "sonner";
import NotFoundCard from "../components/NotFoundCard";

const API = `${window.location.origin}/api/gst`;

const STATUS_COLORS = {
  Active:    { bg: "rgba(34, 197, 94, 0.12)",  border: "#22c55e", text: "#22c55e" },
  Cancelled: { bg: "rgba(239, 68, 68, 0.15)",  border: "#ef4444", text: "#ef4444" },
  Suspended: { bg: "rgba(245, 158, 11, 0.12)", border: "#f59e0b", text: "#f59e0b" },
};

const FLAG_COLORS = {
  critical: { bg: "rgba(220, 38, 38, 0.18)", border: "#dc2626", text: "#fca5a5" },
  high:     { bg: "rgba(239, 68, 68, 0.12)", border: "#ef4444", text: "#ef4444" },
  medium:   { bg: "rgba(245, 158, 11, 0.10)", border: "#f59e0b", text: "#f59e0b" },
  low:      { bg: "rgba(34, 197, 94, 0.10)",  border: "#22c55e", text: "#22c55e" },
};

function StatusBadge({ status }) {
  const c = STATUS_COLORS[status] || STATUS_COLORS.Suspended;
  return (
    <span style={{
      background: c.bg, border: `1px solid ${c.border}`, color: c.text,
      borderRadius: "6px", padding: "4px 10px", fontSize: "11px",
      fontWeight: 700, letterSpacing: "0.4px",
    }}>
      {status}
    </span>
  );
}

function StatCard({ label, value, color, icon: Icon }) {
  return (
    <div style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: "10px", padding: "18px" }}>
      <div className="flex items-center justify-between mb-2">
        <div style={{ color: "#64748b", fontSize: "10px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "1.5px" }}>{label}</div>
        <Icon style={{ width: 16, height: 16, color }} />
      </div>
      <div style={{ color, fontSize: "28px", fontWeight: 700 }}>{value}</div>
    </div>
  );
}

function GSTResultCard({ result, risk, customers, onSave, onClear, saving }) {
  if (!result) return null;
  const isShell = risk?.is_shell_company;
  const accentColor = isShell ? "#dc2626" : (STATUS_COLORS[result.gstin_status]?.border || "#2563eb");

  return (
    <div
      style={{ background: "#0d1117", border: `1px solid ${accentColor}`, borderRadius: "12px", padding: "24px", marginTop: "20px" }}
      data-testid="gst-result-card"
    >
      {/* Top bar */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Building2 style={{ color: accentColor, width: 22, height: 22 }} />
          <div>
            <div style={{ color: "#f1f5f9", fontSize: "18px", fontWeight: 700, lineHeight: 1.2 }}>{result.legal_name || "Unknown"}</div>
            {result.trade_name && result.trade_name !== result.legal_name && (
              <div style={{ color: "#94a3b8", fontSize: "12px" }}>Trade: {result.trade_name}</div>
            )}
          </div>
          <StatusBadge status={result.gstin_status} />
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onClear} data-testid="clear-result-btn" style={{ background: "transparent", border: "1px solid #1e2530", borderRadius: "6px", color: "#94a3b8", padding: "8px 14px", fontSize: "12px", cursor: "pointer" }}>
            Clear
          </button>
          <button
            onClick={onSave}
            disabled={saving}
            data-testid="save-lookup-btn"
            style={{
              background: "linear-gradient(135deg, #2563eb, #1d4ed8)", border: "none", borderRadius: "6px",
              color: "white", padding: "8px 16px", fontWeight: 600, fontSize: "12px",
              cursor: saving ? "wait" : "pointer", display: "flex", alignItems: "center", gap: "6px",
            }}
          >
            {saving ? <Loader2 className="animate-spin" style={{ width: 12, height: 12 }} /> : <FileText style={{ width: 12, height: 12 }} />}
            {saving ? "Saving..." : "Save & Flag"}
          </button>
        </div>
      </div>

      {/* Shell company alert */}
      {isShell && (
        <div style={{ background: "rgba(220, 38, 38, 0.18)", border: "1px solid #dc2626", borderRadius: "8px", padding: "12px 16px", marginBottom: "16px", display: "flex", alignItems: "center", gap: "10px" }}>
          <ShieldAlert style={{ width: 20, height: 20, color: "#fca5a5" }} />
          <div>
            <div style={{ color: "#fca5a5", fontSize: "13px", fontWeight: 700 }}>🚨 SHELL COMPANY DETECTED</div>
            <div style={{ color: "#fca5a5", fontSize: "11px" }}>GSTIN cancelled within 6 months of registration — classic shell-company indicator.</div>
          </div>
        </div>
      )}

      {/* Risk flags */}
      {risk?.flags?.length > 0 && (
        <div style={{ marginBottom: "16px" }}>
          <div style={{ color: "#64748b", fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "1px", marginBottom: "8px" }}>
            Risk Flags ({risk.flags.length}) · +{risk.risk_score_added} risk points
          </div>
          {risk.flags.map((f, idx) => {
            const c = FLAG_COLORS[f.severity] || FLAG_COLORS.medium;
            return (
              <div key={idx} style={{ background: c.bg, border: `1px solid ${c.border}`, borderRadius: "6px", padding: "8px 12px", marginBottom: "6px", display: "flex", alignItems: "center", gap: "8px" }}>
                <AlertTriangle style={{ width: 14, height: 14, color: c.text }} />
                <span style={{ color: c.text, fontSize: "12px", fontWeight: 600 }}>{f.label}</span>
                <span style={{ color: c.text, fontSize: "9px", marginLeft: "auto", textTransform: "uppercase", letterSpacing: "1px" }}>{f.severity}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Data grid */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
        <DataRow icon={Receipt} label="GSTIN" value={result.gstin} mono />
        <DataRow icon={Building2} label="Constitution" value={result.constitution_of_business || result.business_type} />
        <DataRow icon={FileText} label="Nature of Business" value={result.nature_of_business} />
        <DataRow icon={Calendar} label="Registration Date" value={result.registration_date} />
        <DataRow icon={XCircle} label="Cancellation Date" value={result.cancellation_date || "—"} />
        <DataRow icon={MapPin} label="State" value={result.state} />
        <DataRow icon={MapPin} label="District / Pincode" value={`${result.district || "—"} · ${result.pincode || "—"}`} />
        <DataRow icon={FileText} label="Filing Compliance" value={result.filing_compliance} />
        <DataRow icon={Calendar} label="Last Return Filed" value={result.last_return_filed} />
        <DataRow icon={TrendingUp} label="Turnover Slab" value={result.annual_turnover_slab} />
        <DataRow icon={FileText} label="Returns filed (12m)" value={result.returns_filed_count_12m != null ? `${result.returns_filed_count_12m} of 24` : "—"} />
        <DataRow icon={Receipt} label="E-invoice / E-way Bill" value={`${result.e_invoice_enabled ? "Enabled" : "—"} / ${result.e_way_bill_enabled ? "Enabled" : "—"}`} />
      </div>

      {/* Additional places of business */}
      {(result.additional_places_of_business || []).length > 0 && (
        <div style={{ marginTop: 14 }}>
          <div style={{ color: "#64748b", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>
            Additional Places of Business ({result.additional_places_of_business.length})
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
            {result.additional_places_of_business.map((p, i) => (
              <div key={i} data-testid={`gst-branch-${i}`} style={{
                background: "#080c12", border: "1px solid #1e2530",
                borderRadius: 6, padding: "8px 12px",
                color: "#cbd5e1", fontSize: 12,
              }}>
                <MapPin size={11} style={{ display: "inline", marginRight: 6, color: "#64748b" }} />
                {p.address} <span style={{ color: "#64748b" }}>· {p.state}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Returns mini-history */}
      {(result.returns_filed || []).length > 0 && (
        <div style={{ marginTop: 14 }}>
          <div style={{ color: "#64748b", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>
            Recent Returns
          </div>
          <table data-testid="gst-returns-table" style={{ width: "100%", fontSize: 12, color: "#cbd5e1" }}>
            <thead><tr style={{ color: "#64748b", fontSize: 10, textAlign: "left" }}>
              <th>Period</th><th>Form</th><th>Status</th>
            </tr></thead>
            <tbody>
              {result.returns_filed.map((r, i) => (
                <tr key={i} style={{ borderTop: "1px solid #1e2530" }}>
                  <td style={{ padding: "5px 0" }}><code>{r.period}</code></td>
                  <td><code>{r.form}</code></td>
                  <td>
                    <span style={{
                      color: r.status === "Filed" ? "#10b981" : "#f59e0b",
                      fontWeight: 700,
                    }}>{r.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Linked customers */}
      {customers?.length > 0 && (
        <div style={{ borderTop: "1px solid #1e2530", paddingTop: "16px", marginTop: "16px" }}>
          <div style={{ color: "#f1f5f9", fontSize: "12px", fontWeight: 700, marginBottom: "8px" }}>Link to existing customer?</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px" }}>
            {customers.slice(0, 6).map((c) => (
              <div key={c.id} style={{ background: "#080c12", border: "1px solid #1e2530", borderRadius: "6px", padding: "8px 10px", fontSize: "12px", color: "#94a3b8" }}>
                {c.name} <span style={{ color: "#64748b" }}>· {c.risk_level}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ marginTop: "12px", color: "#475569", fontSize: "10px", textAlign: "right" }}>
        AI: {result.ai_mode || "claude-sonnet-4.5"} · Source: {result.source}
      </div>
    </div>
  );
}

function DataRow({ icon: Icon, label, value, mono }) {
  return (
    <div style={{ background: "#080c12", borderRadius: "6px", padding: "10px 12px", display: "flex", alignItems: "center", gap: "10px" }}>
      <Icon style={{ width: 14, height: 14, color: "#64748b" }} />
      <div style={{ flex: 1, overflow: "hidden" }}>
        <div style={{ color: "#64748b", fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.5px" }}>{label}</div>
        <div style={{ color: "#f1f5f9", fontSize: "13px", fontWeight: 600, fontFamily: mono ? "ui-monospace, monospace" : "inherit", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{value || "—"}</div>
      </div>
    </div>
  );
}

export function GSTLookupPanel({ initialCustomerId = null, initialCustomerName = null, onClose = null, compact = false }) {
  const [gstin, setGstin] = useState("");
  const [companyName, setCompanyName] = useState(initialCustomerName || "");
  const [looking, setLooking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [risk, setRisk] = useState(null);
  const [linkCustomerId, setLinkCustomerId] = useState(initialCustomerId);
  const [history, setHistory] = useState([]);
  const [stats, setStats] = useState({ total: 0, cancelled: 0, shell_companies: 0, cases_created: 0 });

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/lookups`, { withCredentials: true });
      setHistory(data.lookups || []);
      setStats(data.stats || { total: 0, cancelled: 0, shell_companies: 0, cases_created: 0 });
    } catch (err) { console.error("[GST] Failed to load history:", err); }
  }, []);

  useEffect(() => { if (!compact) loadHistory(); }, [loadHistory, compact]);

  const runLookup = async () => {
    if (!gstin.trim() && !companyName.trim()) {
      toast.error("Enter GSTIN or company name");
      return;
    }
    setLooking(true); setResult(null); setRisk(null);
    try {
      const { data } = await axios.post(
        `${API}/lookup`,
        { gstin: gstin || null, company_name: companyName || null },
        { withCredentials: true }
      );
      setResult(data.result);
      setRisk(data.risk);
      toast.success(`Lookup complete: ${data.result?.gstin_status || "Unknown"}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Lookup failed");
    } finally {
      setLooking(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await axios.post(
        `${API}/save`,
        { gstin: gstin || null, company_name: companyName || null, result, link_to_customer_id: linkCustomerId },
        { withCredentials: true }
      );
      if (data.created_case) {
        toast.success(`Saved. Case ${data.created_case.case_id} auto-created.`);
      } else if (data.customer_updated) {
        toast.success(`Saved. Customer risk updated to ${data.customer_updated.new_score}/100.`);
      } else {
        toast.success("GST lookup saved.");
      }
      setGstin(""); setCompanyName(initialCustomerName || "");
      setResult(null); setRisk(null);
      if (!compact) loadHistory();
      if (onClose) onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="gst-lookup-panel">
      {/* Stats (full page only) */}
      {!compact && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "16px", marginBottom: "24px" }}>
          <StatCard label="Total Lookups" value={stats.total} color="#2563eb" icon={Building2} />
          <StatCard label="Cancelled" value={stats.cancelled} color="#ef4444" icon={XCircle} />
          <StatCard label="Shell Companies" value={stats.shell_companies} color="#fca5a5" icon={ShieldAlert} />
          <StatCard label="Cases Created" value={stats.cases_created} color="#f59e0b" icon={FileText} />
        </div>
      )}

      {/* Input */}
      <div style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: "12px", padding: compact ? "16px" : "24px" }}>
        <div className="flex items-center gap-2 mb-4">
          <Sparkles style={{ width: 18, height: 18, color: "#2563eb" }} />
          <h2 style={{ fontSize: compact ? "14px" : "16px", fontWeight: 700, margin: 0, color: "#f1f5f9" }}>
            {compact ? "GST Quick Verify" : "Verify Company via GST Portal AI"}
          </h2>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "12px" }}>
          <input
            type="text"
            value={gstin}
            onChange={(e) => setGstin(e.target.value.toUpperCase())}
            placeholder="GSTIN (e.g. 27AAACR5055K1Z5)"
            maxLength={15}
            data-testid="gstin-input"
            style={{ background: "#080c12", border: "1px solid #1e2530", borderRadius: "8px", padding: "10px 14px", color: "#f1f5f9", fontSize: "13px", fontFamily: "ui-monospace, monospace" }}
          />
          <input
            type="text"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            placeholder="OR Company Name"
            data-testid="company-name-input"
            style={{ background: "#080c12", border: "1px solid #1e2530", borderRadius: "8px", padding: "10px 14px", color: "#f1f5f9", fontSize: "13px" }}
          />
        </div>

        <div className="flex items-center justify-between">
          <div style={{ color: "#64748b", fontSize: "11px" }}>Powered by Claude Sonnet 4.5 · Source: api.gst.gov.in</div>
          <button
            onClick={runLookup}
            disabled={looking}
            data-testid="lookup-btn"
            style={{
              background: looking ? "#1e2530" : "linear-gradient(135deg, #2563eb, #1d4ed8)",
              border: "none", borderRadius: "8px", color: "white", padding: "10px 22px",
              fontWeight: 600, fontSize: "13px", cursor: looking ? "wait" : "pointer",
              display: "flex", alignItems: "center", gap: "8px",
            }}
          >
            {looking ? (
              <><Loader2 className="animate-spin" style={{ width: 14, height: 14 }} /> Verifying...</>
            ) : (
              <><Search style={{ width: 14, height: 14 }} /> Lookup GST</>
            )}
          </button>
        </div>
      </div>

      {/* Result */}
      {result && result.found === false && <NotFoundCard result={result} />}
      {result && result.found !== false && (
        <GSTResultCard
          result={result}
          risk={risk}
          customers={[]}
          onSave={handleSave}
          onClear={() => { setResult(null); setRisk(null); }}
          saving={saving}
        />
      )}

      {/* History (full page only) */}
      {!compact && (
        <div style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: "12px", padding: "24px", marginTop: "24px" }}>
          <h2 style={{ fontSize: "16px", fontWeight: 700, margin: "0 0 16px", color: "#f1f5f9" }}>Recent Lookups ({history.length})</h2>
          {history.length === 0 ? (
            <div style={{ color: "#64748b", fontSize: "13px", textAlign: "center", padding: "32px" }}>
              No lookups yet. Try lookup karein "Infosys" ya "Mehul Choksi" se shell-company detection dekho.
            </div>
          ) : (
            <div style={{ display: "grid", gap: "8px" }}>
              {history.map((l) => {
                const r = l.result || {};
                return (
                  <div key={l.id} style={{ background: "#080c12", border: "1px solid #1e2530", borderRadius: "8px", padding: "12px 16px", display: "grid", gridTemplateColumns: "1fr auto auto auto auto", alignItems: "center", gap: "16px" }}>
                    <div>
                      <div style={{ color: "#f1f5f9", fontSize: "13px", fontWeight: 600 }}>{r.legal_name || l.company_name || "Unknown"}</div>
                      <div style={{ color: "#64748b", fontSize: "10px", fontFamily: "ui-monospace, monospace" }}>{r.gstin || l.gstin || "—"}</div>
                    </div>
                    {l.risk?.is_shell_company && (
                      <span style={{ background: "rgba(220, 38, 38, 0.18)", color: "#fca5a5", borderRadius: "4px", padding: "2px 8px", fontSize: "10px", fontWeight: 700 }}>SHELL</span>
                    )}
                    <div style={{ color: "#94a3b8", fontSize: "11px" }}>{r.filing_compliance || "—"}</div>
                    <div style={{ color: "#64748b", fontSize: "10px" }}>{new Date(l.created_at).toLocaleDateString()}</div>
                    <StatusBadge status={r.gstin_status} />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function GSTLookupPage() {
  return (
    <div data-testid="gst-lookup-page" style={{ color: "#f1f5f9" }}>
      <div className="flex items-center gap-3 mb-2">
        <Building2 style={{ width: 26, height: 26, color: "#2563eb" }} />
        <h1 style={{ fontSize: "26px", fontWeight: 700, margin: 0, color: "#f1f5f9" }} data-testid="page-title">GST Portal Lookup</h1>
      </div>
      <p style={{ color: "#94a3b8", fontSize: "13px", marginBottom: "24px" }}>
        Verify any Indian company via GST Portal AI. Auto-detects shell companies (cancelled within 6 months) and creates HIGH/CRITICAL cases.
      </p>
      <GSTLookupPanel />
    </div>
  );
}
